import { useEffect, useState } from "react";
import { fetchWithToken } from "../utils/fetchWithToken";

const parsePercentage = (str) => parseFloat(str?.replace('%', '').replace(',', '.')) || 0;
const parseCurrency = (str) => parseFloat(str?.replace('R$', '').replace(',', '.')) || 0;

export const useFetchCampaigns = (funil = "funilEcommerce") => {
  const [campaigns, setCampaigns] = useState([]);
  const [averages, setAverages] = useState(null);
  const [totalAverages, setTotalAverages] = useState(null);
  const [rawCampaigns, setRawCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const selectedClientId = localStorage.getItem("selectedClientId");

  useEffect(() => {
    const fetchCampaigns = async () => {
      setIsLoading(true);
      try {
        const response = await fetchWithToken(
          `https://ad-insights-api-0f2fcea0deda.herokuapp.com/api/campaign-insight/campaigns-file?clientId=${selectedClientId}&funil=${funil}`
        );

        if (!response.ok) throw new Error(`Erro HTTP: ${response.status}`);

        const data = await response.json();
        const campaignsArray = data?.data?.campaigns || [];
        const totalAverages = data?.data?.averages || {};

        const formatted = campaignsArray.map((c, index) => ({
          id: index,
          name: c.nomeDaCampanha,
          ctr: c.ctr != null ? String(c.ctr) : "0%",
          cpc: c.cpc != null ? String(c.cpc) : "R$0,00",
          roas: c.roas != null ? String(c.roas) : "R$0,00",
          conversionRate: c.taxaDeConversao != null ? String(c.taxaDeConversao) : "0%",
          prioridade: c.prioridade || "N/A",
          target: c.target || "N/A",
          compras: c.compras != null ? String(c.compras) : "R$0,00",
          inicioDosRelatorios: c.inicioDosRelatorios || "N/A",
          terminoDosRelatorios: c.terminoDosRelatorios || "N/A",
        }));

        const calculatedAverages = calculateAverages(formatted);

        setTotalAverages(totalAverages);
        setAverages(calculatedAverages);
        setRawCampaigns(campaignsArray);
        setCampaigns(formatted);
      } catch (error) {
        console.error("Erro ao buscar campanhas:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCampaigns();
  }, [selectedClientId, funil]);

  const calculateAverages = (campaigns) => {
    const total = {
      conversionRate: 0,
      ctr: 0,
      cpc: 0,
      roas: 0,
      compras: 0,
    };
    let count = campaigns.length;

    campaigns.forEach((c) => {
      total.conversionRate += parsePercentage(c.conversionRate);
      total.ctr += parsePercentage(c.ctr);
      total.cpc += parseCurrency(c.cpc);
      total.roas += parseCurrency(c.roas);
      total.compras += parseCurrency(c.compras);
    });

    return {
      avgConversionRate: total.conversionRate / count,
      avgCtr: total.ctr / count,
      avgCpc: total.cpc / count,
      avgCompras: total.compras / count,
      avgRoas: total.roas / count,
    };
  };

  const getComparison = (campaign) => {
    if (!averages) return null;

    const conversionRateNumber = parsePercentage(campaign.conversionRate);
    const ctrNumber = parsePercentage(campaign.ctr);
    const cpcNumber = parseCurrency(campaign.cpc);
    const roasNumber = parseCurrency(campaign.roas);
    const comprasNumber = parseCurrency(campaign.compras);

    const comparison = {
      conversionRate: conversionRateNumber >= averages.avgConversionRate ? "acima" : "abaixo",
      ctr: ctrNumber >= averages.avgCtr ? "acima" : "abaixo",
      cpc: cpcNumber <= averages.avgCpc ? "abaixo" : "acima",
      roas: roasNumber >= averages.avgRoas ? "acima" : "abaixo",
      compras: comprasNumber >= averages.avgCompras ? "acima" : "abaixo",
    };

    const diffPercentages = {
      conversionRate: averages.avgConversionRate
        ? Math.abs(((conversionRateNumber - averages.avgConversionRate) / averages.avgConversionRate) * 100)
        : 0,
      ctr: averages.avgCtr
        ? Math.abs(((ctrNumber - averages.avgCtr) / averages.avgCtr) * 100)
        : 0,
      cpc: averages.avgCpc
        ? Math.abs(((cpcNumber - averages.avgCpc) / averages.avgCpc) * 100)
        : 0,
      roas: averages.avgRoas
        ? Math.abs(((roasNumber - averages.avgRoas) / averages.avgRoas) * 100)
        : 0,
      compras: averages.avgCompras
        ? Math.abs(((comprasNumber - averages.avgCompras) / averages.avgCompras) * 100)
        : 0,
    };

    return { comparison, diffPercentages };
  };

  return {
    campaigns,
    averages,
    totalAverages,
    rawCampaigns,
  };
};
